import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/AdminShared.css";

const sections = [
  {
    title: "Your Business",
    fields: [
      { name: "businessName", label: "What's the name of your business?", type: "input" },
      { name: "businessSummary", label: "Give a short summary of your business (1–2 sentences).", type: "textarea" },
      { name: "businessStory", label: "Why does your business exist? (The story behind your business and core values.)", type: "textarea" },
      { name: "customerProblems", label: "What problems are you solving for your customers?", type: "textarea" },
      { name: "uniqueSellingPoints", label: "Why do clients/customers choose you? What sets you apart?", type: "textarea" },
      { name: "businessGoals", label: "What are your goals for the business over the next year?", type: "textarea" },
    ],
  },
  {
    title: "Your Audience",
    fields: [
      { name: "idealCustomer", label: "Who is your ideal customer?", type: "textarea" },
      { name: "audienceChallenges", label: "What specific challenges does your audience face that your business helps solve?", type: "textarea" },
      { name: "audienceEmotions", label: "What emotions or feelings do you want your audience to associate with your brand?", type: "textarea" },
      { name: "competitors", label: "Do you have any competitors? (List them and explain how your business is different.)", type: "textarea" },
    ],
  },
  {
    title: "Your Design",
    fields: [
      { name: "preferredColors", label: "What are your preferred colors for your brand?", type: "input", placeholder: "E.g., Blue, White, Gold" },
      { name: "preferredFonts", label: "Do you have any specific fonts or typography in mind for your branding?", type: "input" },
      { name: "brandStyle", label: "What style best represents your brand? (E.g., modern, classic, playful, professional.)", type: "textarea" },
      { name: "brandInspiration", label: "Do you have any inspirational ideas, images, or references that represent your vision?", type: "textarea" },
      { name: "additionalNotes", label: "Any additional notes or ideas about how your brand should look and feel?", type: "textarea" },
    ],
  },
];

const AdminBrandQuestionnaire = () => {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState("Loading...");
  const [formData, setFormData] = useState({
    businessName: "", businessSummary: "", businessStory: "", customerProblems: "",
    uniqueSellingPoints: "", businessGoals: "", idealCustomer: "", audienceChallenges: "",
    audienceEmotions: "", competitors: "", preferredColors: "", preferredFonts: "",
    brandStyle: "", brandInspiration: "", additionalNotes: "",
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        if (!projectId) throw new Error("Project ID is missing.");
        const projectRef = doc(db, "projects", projectId);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const data = projectSnap.data();
          setProjectName(data.projectName || "Unnamed Project");
          if (data.brandQuestionnaire) setFormData(data.brandQuestionnaire);
        } else {
          throw new Error("Project not found.");
        }
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load project data: " + err.message);
      } finally {
        setPageLoading(false);
      }
    };
    fetchProjectData();
  }, [projectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.businessName || !formData.businessSummary) {
      setError("Please fill out all required fields.");
      toast.error("Please fill out all required fields.");
      return;
    }
    setLoading(true);
    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, { brandQuestionnaire: formData });
      toast.success("Questionnaire updated successfully!");
      setError("");
    } catch (err) {
      setError("Failed to update responses: " + err.message);
      toast.error("Failed to update responses: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) return <div className="ap-loading"><p>Loading...</p></div>;

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <p className="ap-eyebrow">Admin — Brand</p>
        <h1 className="ap-title">{projectName}: Brand Questionnaire</h1>
        <p className="ap-subtitle">View or edit the client's brand questionnaire responses.</p>
      </div>

      <form onSubmit={handleSubmit}>
        {sections.map((section) => (
          <div key={section.title} className="ap-section" style={{ marginBottom: '20px' }}>
            <div className="ap-section-header">
              <h2 className="ap-section-title">{section.title}</h2>
            </div>
            <div className="ap-section-body">
              {section.fields.map(({ name, label, type, placeholder }) => (
                <div key={name} className="ap-form-group">
                  <label>{label}</label>
                  {type === "input" ? (
                    <input type="text" name={name} value={formData[name]} onChange={handleChange} placeholder={placeholder || ""} />
                  ) : (
                    <textarea name={name} value={formData[name]} onChange={handleChange} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <p className="ap-message--error" style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', marginBottom: '16px' }}>{error}</p>}

        <div className="ap-btn-row">
          <button type="submit" className="ap-btn-primary" disabled={loading || !projectId}>
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminBrandQuestionnaire;
